import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: number;
  username: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
  shop_name: string | null;
  shop_id: number | null;
}

const AllUsers: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterShop, setFilterShop] = useState<string>('');
  const [shops, setShops] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (user?.role === 'owner') {
      fetchUsers();
      fetchShops();
    }
  }, [user, filterRole, filterShop]);

  const fetchShops = async () => {
    try {
      const response = await fetch('/api/owner/shops', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setShops(data);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (filterRole) params.append('role', filterRole);
      if (filterShop) params.append('shopId', filterShop);
      
      const response = await fetch(`/api/owner/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId: number) => {
    const newPassword = prompt('Введите новый пароль для пользователя:');
    if (!newPassword || newPassword.length < 6) {
      alert('Пароль должен содержать минимум 6 символов');
      return;
    }
    
    try {
      const response = await fetch(`/api/owner/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ newPassword })
      });

      if (response.ok) {
        alert('Пароль успешно изменен');
      } else {
        const errorData = await response.json();
        alert(`Ошибка: ${errorData.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Ошибка при сбросе пароля');
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Глобальный администратор';
      case 'admin': return 'Администратор магазина';
      case 'operator': return 'Оператор';
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'operator': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Активен
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Заблокирован
      </span>
    );
  };

  if (user?.role !== 'owner') {
    return <div className="text-center text-red-600">Доступ запрещен</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Все пользователи системы</h2>
        <p className="text-gray-600">Управление всеми пользователями системы, включая администраторов магазинов и операторов</p>
      </div>

      {/* Фильтры */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Роль</label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Все роли</option>
              <option value="owner">Глобальный администратор</option>
              <option value="admin">Администратор магазина</option>
              <option value="operator">Оператор</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Магазин</label>
            <select
              value={filterShop}
              onChange={(e) => setFilterShop(e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Все магазины</option>
              {shops.map(shop => (
                <option key={shop.id} value={shop.id}>{shop.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilterRole('');
                setFilterShop('');
              }}
              className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
            >
              Сбросить фильтры
            </button>
          </div>
        </div>
      </div>

      {/* Список пользователей */}
      {loading ? (
        <div className="text-center py-8">Загрузка...</div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Пользователи ({users.length})
              </h3>
            </div>
          </div>
          
          {users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Пользователи не найдены
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {users.map((user) => (
                <li key={user.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-lg font-medium text-gray-900">{user.username}</h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                        {getStatusBadge(user.is_active)}
                      </div>
                      
                      <div className="mt-2 space-y-1">
                        {user.shop_name && (
                          <p className="text-sm text-gray-600">
                            Магазин: <span className="font-medium">{user.shop_name}</span>
                          </p>
                        )}
                        <p className="text-sm text-gray-500">
                          Создан: {new Date(user.created_at).toLocaleDateString('ru-RU')}
                        </p>
                        {user.updated_at && (
                          <p className="text-sm text-gray-500">
                            Обновлен: {new Date(user.updated_at).toLocaleDateString('ru-RU')}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleResetPassword(user.id)}
                        className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm"
                      >
                        Сменить пароль
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AllUsers;
